import {
  concatenateDataTypeNameWithSelectedSegmentType,
  getCountryFromSideFilters,
  getProductGroupFromSideFilters,
  segmentsFilterWithExcludeFilterKeys,
} from '@utils/FilterUtils';
import { FilterKeys, Filters, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import {
  concatenateAllSelectedSegmentFeatureName,
  concatenateSelectedFeatureName,
  doAmplitudeAnalyticsTracking,
} from '../PageAnalytics';
import { AnalyticTrackingBasePropertiesObj, TrackingGeneralProp } from './AnalyticTrackingBasePropertiesObj';

//Example of tracking for - Page Element Clicked
/**
 analytics.track("Page Element Clicked", {
 "access": "Retail Insights",
 "retailerDataFor": "Dixons Carphone",
 "locationId": "Germany",
 "elementName": "View Toggle Clicked",
 "chartName": "Opportunity Review for Retailer"
 "optionChosen": "Bubble Chart" //Table
 "pageName": "Opportunity Review",
 "itemGroup": "PTV",
 "section": "Retail Intelligence",
 "subsection": "Opportunity Review",
 "elementCategory": "Page link"
 })
 **/

export const PAGE_ELEMENT_CLICK_TRACKING_TITLE = 'Page Element Clicked';
export const TOGGLE_CLICKED_TITLE = 'View Toggle Clicked';
export const SAVE_FILTER_BTN = 'Save filter';
export const SAVE_FILTER_DIALOG_BTN = 'Filter Saved';
export const ELEMENT_CATEGORY_BTN = 'button';

export const ELEMENT_CATEGORY = 'Page link';

export const PAGE_ELEMENT_CATEGORY_CTA = 'CTA';
export const PAGE_ELEMENT_TYPE = 'Link';

//Extend base properties
export interface PageElementClickedTracking extends AnalyticTrackingBasePropertiesObj {
  chartName?: string;
  elementName: string;
  elementCategory?: string;
  elementType?: string;
  categoryCombination?: string;
  optionChosen?: string;
}

export interface PageElementClickTrackingParam {
  eventTitle: string;
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  chartName?: string;
  elementName: string;
  categoryCombination?: string;
  optionChosen?: string;
  elementCategory?: string;
  elementType?: string;
  access: Object;
  subSection?: SubSection;
}

export const pageElementClickTrackingFunction = (param: PageElementClickTrackingParam) => {
  const {
    sideFilters,
    pageName,
    retailerClientState,
    pageType,
    chartName,
    track,
    eventTitle,
    categoryCombination,
    optionChosen,
    elementName,
    access,
    elementCategory,
    elementType,
    subSection,
  } = param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const pageElementViewProperties: PageElementClickedTracking = {
    access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label),
    retailerDataFor: retailerClientState?.clientName,
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: subSection ?? pageName,
    ...(optionChosen ? { optionChosen } : {}),
    categoryCombination: categoryCombination,
    ...(chartName ? { chartName } : {}),
    elementName,
    ...(elementCategory && { elementCategory }),
    ...(elementType && { elementType }),
  };

  setCategoryCombination(param, pageElementViewProperties);
  setOptionChosen(param, pageElementViewProperties);

  doAmplitudeAnalyticsTracking(eventTitle, track, pageElementViewProperties);
};

/**
 * Updates the `optionChosen` property of the `pageElementViewProperties` object.
 *
 * @param param - The parameters for tracking page element clicks, including filters and element details.
 * @param pageElementViewProperties - The object representing tracking properties for a clicked element.
 */
const setOptionChosen = (
  param: PageElementClickTrackingParam,
  pageElementViewProperties: PageElementClickedTracking,
): void => {
  const { sideFilters, pageType, optionChosen, elementName } = param;

  // Direct assignment if the element is not a save filter button
  if (![SAVE_FILTER_BTN, SAVE_FILTER_DIALOG_BTN].includes(elementName)) {
    pageElementViewProperties.optionChosen = optionChosen;
    return;
  }

  // Process and assign filtered segment feature names
  const selectedSideFilter = sideFilters[pageType];
  const filteredSegments: Filters[] | undefined = segmentsFilterWithExcludeFilterKeys(selectedSideFilter, [
    FilterKeys.COUNTRY,
    FilterKeys.PRODUCT_GROUP,
  ]);
  pageElementViewProperties.optionChosen = concatenateAllSelectedSegmentFeatureName(filteredSegments);
};

/**
 * Updates the `categoryCombination` property of the `pageElementViewProperties` object.
 *
 * @param param - The parameters for tracking page element clicks, including filters and element details.
 * @param pageElementViewProperties - The object representing tracking properties for a clicked element.
 */
const setCategoryCombination = (
  param: PageElementClickTrackingParam,
  pageElementViewProperties: PageElementClickedTracking,
): void => {
  const { sideFilters, pageType, categoryCombination, elementName } = param;

  // Direct assignment if the element is not a save filter button
  if (![SAVE_FILTER_BTN, SAVE_FILTER_DIALOG_BTN].includes(elementName)) {
    pageElementViewProperties.categoryCombination = categoryCombination;
    return;
  }

  // Process and assign filtered segment combinations
  const selectedSideFilter = sideFilters[pageType];
  const filteredSegments: Filters[] | undefined = segmentsFilterWithExcludeFilterKeys(selectedSideFilter, [
    FilterKeys.COUNTRY,
    FilterKeys.PRODUCT_GROUP,
  ]);
  pageElementViewProperties.categoryCombination = concatenateDataTypeNameWithSelectedSegmentType(
    filteredSegments,
    false,
  );
};
